import { SlideSection } from "@NextSkyTheme/slide";
import { LazyLoader } from "@NextSkyTheme/lazy-load";
import { eventModal } from "@NextSkyTheme/modal";
import PhotoSwipeLightbox from "@NextSkyTheme/photo-swipe-lightbox";
import { CustomElement } from "@NextSkyTheme/safari-element-patch";
import * as NextSkyTheme from "@NextSkyTheme/global";

class MediaGallery extends SlideSection {
  constructor() {
    super();
    this.init();
  }

  get dataImageZoom() {
    return this.hasAttribute("data-image-zoom")
      ? this.getAttribute("data-image-zoom")
      : "none";
  }

  init() {
    if (
      this.querySelector(".product__media-gallery").classList.contains(
        "product-thumbnail"
      )
    ) {
      let thumbnail = this.initSlideMediaGallery("thumbnail");
      const mainSwiper = this.initSlideMediaGallery("main", thumbnail);

      // Wacht tot Swiper volledig geïnitialiseerd is
      if (mainSwiper) {
        // Gebruik once om dubbele binding te voorkomen
        const setupAfterInit = () => {
          this.setupThumbnailClicks(mainSwiper, thumbnail);
          this.setupMainImageLightbox(mainSwiper);
          this.setupNavigationButtons(mainSwiper);
        };
        
        if (mainSwiper.initialized) {
          setupAfterInit();
        } else {
          mainSwiper.once("init", setupAfterInit);
        }
      }
    }

    let inlinePane = 767;
    if (this.dataImageZoom == "zoom_inner") {
      inlinePane = 4800;
    }
  }
  
  setupThumbnailClicks(mainSwiper, thumbnailSwiper) {
    if (!mainSwiper) return;

    const thumbnailContainer = this.querySelector(".media-thumb-swiper");
    if (!thumbnailContainer) return;

    const allThumbnailSlides = Array.from(
      thumbnailContainer.querySelectorAll(".swiper-slide")
    );
    if (!allThumbnailSlides.length) return;

    // Bepaal totaal aantal slides (zonder duplicates)
    const getTotalSlides = () => {
      const realSlides = mainSwiper.slides.filter(
        (slide) => !slide.classList.contains("swiper-slide-duplicate")
      );
      return realSlides.length || mainSwiper.slides.length;
    };

    const totalRealSlides = getTotalSlides();

    // Functie om de echte index van een thumbnail slide te bepalen
    const getRealIndexForSlide = (slide) => {
      if (!slide) return null;
      
      // Probeer eerst data-position attribute (meest betrouwbaar)
      const dataPosition = slide.querySelector("[data-position]");
      if (dataPosition) {
        const position = parseInt(dataPosition.getAttribute("data-position"), 10);
        if (!Number.isNaN(position) && position > 0) {
          return position - 1; // Convert to 0-based index
        }
      }
      
      // Fallback: gebruik swiper-slide-index
      const dataIndex = slide.getAttribute("data-swiper-slide-index");
      if (dataIndex !== null) {
        const parsed = parseInt(dataIndex, 10);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
      
      // Laatste fallback: gebruik positie in array (alleen als geen duplicates)
      const index = allThumbnailSlides.indexOf(slide);
      return index >= 0 ? index : null;
    };

    // Navigeer naar specifieke slide index
    const navigateToIndex = (targetIndex) => {
      if (
        typeof targetIndex !== "number" ||
        targetIndex < 0 ||
        targetIndex >= totalRealSlides
      ) {
        return;
      }
      if (mainSwiper._thumbLock) return;

      mainSwiper._thumbLock = true;
      const isLoop = mainSwiper.params.loop;
      
      // Instant transition voor thumbnail clicks
      mainSwiper.setTransition(0);
      
      if (isLoop && typeof mainSwiper.slideToLoop === "function") {
        mainSwiper.slideToLoop(targetIndex, 0);
      } else {
        mainSwiper.slideTo(targetIndex, 0);
      }

      queueMicrotask(() => {
        mainSwiper._thumbLock = false;
        // Reset naar smooth voor volgende swipe
        mainSwiper.setTransition(300);
      });
    };

    // Setup click handlers voor thumbnails
    // Swiper Thumbs zou dit moeten doen, maar we voegen expliciete handlers toe als backup
    const firstSlidesPerIndex = new Map();
    allThumbnailSlides.forEach((thumbSlide) => {
      const realIndex = getRealIndexForSlide(thumbSlide);
      if (realIndex === null) return;

      // Zorg dat thumbnails klikbaar zijn
      thumbSlide.style.pointerEvents = "auto";
      thumbSlide.style.cursor = "pointer";
      thumbSlide.style.touchAction = "manipulation";

      if (!firstSlidesPerIndex.has(realIndex)) {
        firstSlidesPerIndex.set(realIndex, thumbSlide);
        thumbSlide.setAttribute(
          "aria-label",
          `Toon afbeelding ${realIndex + 1} van ${totalRealSlides}`
        );
        thumbSlide.setAttribute("role", "button");
        thumbSlide.setAttribute("tabindex", "0");

        // Click handler voor thumbnail
        const handleClick = (event) => {
          // Negeer clicks op child elementen (zoals images)
          if (event.target !== thumbSlide && !thumbSlide.contains(event.target)) {
            return;
          }
          
          event.preventDefault();
          event.stopPropagation();
          navigateToIndex(realIndex);
        };

        // Pointer handler voor betere touch support
        const handlePointerUp = (event) => {
          if (event.target !== thumbSlide && !thumbSlide.contains(event.target)) {
            return;
          }
          
          event.preventDefault();
          event.stopPropagation();
          navigateToIndex(realIndex);
        };

        // Keyboard handler
        const handleKey = (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            navigateToIndex(realIndex);
          }
        };

        // Cleanup oude handlers
        if (thumbSlide._thumbClickHandler) {
          thumbSlide.removeEventListener("click", thumbSlide._thumbClickHandler);
        }
        if (thumbSlide._thumbPointerHandler) {
          thumbSlide.removeEventListener("pointerup", thumbSlide._thumbPointerHandler);
        }
        if (thumbSlide._thumbKeyHandler) {
          thumbSlide.removeEventListener("keydown", thumbSlide._thumbKeyHandler);
        }

        thumbSlide._thumbClickHandler = handleClick;
        thumbSlide._thumbPointerHandler = handlePointerUp;
        thumbSlide._thumbKeyHandler = handleKey;

        thumbSlide.addEventListener("click", handleClick, { passive: false });
        thumbSlide.addEventListener("pointerup", handlePointerUp, { passive: false });
        thumbSlide.addEventListener("keydown", handleKey);
      } else {
        // Duplicate slide - alleen aria, geen handlers
        if (thumbSlide._thumbClickHandler) {
          thumbSlide.removeEventListener("click", thumbSlide._thumbClickHandler);
          thumbSlide._thumbClickHandler = null;
        }
        if (thumbSlide._thumbPointerHandler) {
          thumbSlide.removeEventListener("pointerup", thumbSlide._thumbPointerHandler);
          thumbSlide._thumbPointerHandler = null;
        }
        if (thumbSlide._thumbKeyHandler) {
          thumbSlide.removeEventListener("keydown", thumbSlide._thumbKeyHandler);
          thumbSlide._thumbKeyHandler = null;
        }
        thumbSlide.setAttribute("tabindex", "-1");
        thumbSlide.setAttribute("aria-hidden", "true");
        thumbSlide.removeAttribute("role");
      }
    });

    // Update active state van thumbnails
    const updateAriaCurrent = () => {
      const currentIndex =
        mainSwiper.params.loop && typeof mainSwiper.realIndex === "number"
          ? mainSwiper.realIndex
          : mainSwiper.activeIndex;

      allThumbnailSlides.forEach((slide) => {
        const slideIndex = getRealIndexForSlide(slide);
        if (slideIndex === currentIndex) {
          slide.setAttribute("aria-current", "true");
          slide.classList.add("swiper-slide-thumb-active");
        } else {
          slide.removeAttribute("aria-current");
          slide.classList.remove("swiper-slide-thumb-active");
        }
      });
    };

    // Cleanup oude handlers
    if (mainSwiper._thumbAriaHandler) {
      mainSwiper.off("slideChange", mainSwiper._thumbAriaHandler);
      mainSwiper.off("realIndexChange", mainSwiper._thumbAriaHandler);
    }
    mainSwiper._thumbAriaHandler = updateAriaCurrent;
    mainSwiper.on("slideChange", updateAriaCurrent);
    mainSwiper.on("realIndexChange", updateAriaCurrent);
    updateAriaCurrent();
  }

  // Setup navigatiepijlen: verberg links op eerste slide, rechts op laatste slide
  setupNavigationButtons(mainSwiper) {
    if (!mainSwiper) return;

    const navContainer = this.querySelector(".media-gallery__nav");
    if (!navContainer) return;

    const prevBtn = navContainer.querySelector(".swiper-button-prev");
    const nextBtn = navContainer.querySelector(".swiper-button-next");
    if (!prevBtn || !nextBtn) return;

    const updateButtonVisibility = () => {
      const totalSlides = () => {
        const realSlides = mainSwiper.slides.filter(
          (slide) => !slide.classList.contains("swiper-slide-duplicate")
        );
        return realSlides.length || mainSwiper.slides.length;
      };

      const total = totalSlides();
      const isLoop = mainSwiper.params.loop;
      
      if (total <= 1) {
        // Verberg beide pijlen als er maar 1 slide is
        prevBtn.style.display = "none";
        nextBtn.style.display = "none";
        return;
      }

      let currentIndex;
      if (isLoop && typeof mainSwiper.realIndex === "number") {
        currentIndex = mainSwiper.realIndex;
      } else {
        currentIndex = mainSwiper.activeIndex;
      }

      // Toon/verberg prev button
      if (currentIndex === 0 && !isLoop) {
        prevBtn.style.display = "none";
        prevBtn.classList.add("swiper-button-disabled");
        prevBtn.disabled = true;
      } else {
        prevBtn.style.display = "";
        prevBtn.classList.remove("swiper-button-disabled");
        prevBtn.disabled = false;
      }

      // Toon/verberg next button
      if (currentIndex >= total - 1 && !isLoop) {
        nextBtn.style.display = "none";
        nextBtn.classList.add("swiper-button-disabled");
        nextBtn.disabled = true;
      } else {
        nextBtn.style.display = "";
        nextBtn.classList.remove("swiper-button-disabled");
        nextBtn.disabled = false;
      }
    };

    // Update bij slide change
    if (mainSwiper._navVisibilityHandler) {
      mainSwiper.off("slideChange", mainSwiper._navVisibilityHandler);
      mainSwiper.off("realIndexChange", mainSwiper._navVisibilityHandler);
    }
    mainSwiper._navVisibilityHandler = updateButtonVisibility;
    mainSwiper.on("slideChange", updateButtonVisibility);
    mainSwiper.on("realIndexChange", updateButtonVisibility);
    
    // Initial update
    updateButtonVisibility();
  }

  setupMainImageLightbox(mainSwiper) {
    if (!mainSwiper) return;

    // Eén gedeelde swipe-flag voor de hele gallery: een touch die als swipe
    // eindigt op slide A kan een synthetische click op slide B veroorzaken
    // (vinger laat daar los). Per-slide flags missen dat.
    this._swipeBlockUntil = 0;

    // Wacht tot Swiper volledig geladen is
    const setupLightbox = () => {
      const imageSlides = this.getImageSlides();
      if (!imageSlides.length) {
        // Retry na korte delay als slides nog niet geladen zijn
        setTimeout(() => {
          const retrySlides = this.getImageSlides();
          if (retrySlides.length) {
            setupLightbox();
          }
        }, 100);
        return;
      }

      imageSlides.forEach((slide, index) => {
        // Cleanup oude handlers
        if (slide._lightboxClickHandler) {
          slide.removeEventListener("click", slide._lightboxClickHandler);
          slide.removeEventListener("touchstart", slide._lightboxTouchStartHandler);
          slide.removeEventListener("touchend", slide._lightboxTouchEndHandler);
        }

        slide.style.cursor = "pointer";
        slide.style.pointerEvents = "auto";
        slide.setAttribute("role", "button");
        slide.setAttribute("tabindex", "0");

        // Bepaal de juiste index op basis van realIndex (bij loop) of activeIndex
        const getRealIndex = () => {
          if (mainSwiper.params.loop && typeof mainSwiper.realIndex === "number") {
            return mainSwiper.realIndex;
          }
          return mainSwiper.activeIndex;
        };

        const openAtIndex = (event) => {
          // Disable lightbox on desktop (width >= 1025px)
          if (window.innerWidth >= 1025) return;

          // Negeer kliks op navigatiepijlen of andere interactieve elementen
          if (event && event.target) {
            const target = event.target;
            if (target.closest('.swiper-button-next, .swiper-button-prev, .media-gallery__nav, .pswp')) {
              return;
            }
          }

          if (mainSwiper.animating || mainSwiper._lightboxLock) return;
          mainSwiper._lightboxLock = true;

          // Gebruik de image als opener element voor zoom-in animatie
          const image = slide.querySelector("img");
          
          // Bepaal de juiste index: gebruik de index in imageSlides array
          // Deze array bevat al geen duplicates, dus index is direct bruikbaar
          const targetIndex = index;
          
          if (image) {
            this.openLightbox(targetIndex, image);
          } else {
            this.openLightbox(targetIndex);
          }
          
          requestAnimationFrame(() => {
            mainSwiper._lightboxLock = false;
          });
        };
        let touchStartX = 0;
        let touchStartY = 0;
        const gallery = this;

        const handleTouchStart = (e) => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        };

        const handleTouchEnd = (e) => {
          const touchEndX = e.changedTouches[0].clientX;
          const touchEndY = e.changedTouches[0].clientY;
          const dx = Math.abs(touchEndX - touchStartX);
          const dy = Math.abs(touchEndY - touchStartY);

          if (dx < 10 && dy < 10) {
            // Tap: open lightbox en blokkeer de synthetische click die volgt
            e.preventDefault();
            e.stopPropagation();
            openAtIndex(e);
          } else {
            // Swipe: blokkeer de synthetische click die de browser hierna
            // kan vuren — die kan op een andere slide landen dan deze.
            gallery._swipeBlockUntil = Date.now() + 500;
          }
        };

        const handleClick = (e) => {
          if (Date.now() < (gallery._swipeBlockUntil || 0) || mainSwiper.animating) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          openAtIndex(e);
        };

        slide._lightboxTouchStartHandler = handleTouchStart;
        slide._lightboxTouchEndHandler = handleTouchEnd;
        slide._lightboxClickHandler = handleClick;

        slide.addEventListener("touchstart", handleTouchStart, { passive: true });
        slide.addEventListener("touchend", handleTouchEnd, { passive: false });
        slide.addEventListener("click", handleClick, { passive: false });

        // Keyboard support
        const handleKey = (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            openAtIndex(event);
          }
        };

        slide.addEventListener("keydown", handleKey);
      });
    };

    // Wacht tot Swiper geïnitialiseerd is
    if (mainSwiper.initialized) {
      setupLightbox();
    } else {
      mainSwiper.once("init", () => {
        setupLightbox();
      });
    }

    // Herbind bij slide change (voor nieuwe slides bij loop)
    mainSwiper.on("slideChange", () => {
      const imageSlides = this.getImageSlides();
      imageSlides.forEach((slide) => {
        if (!slide._lightboxClickHandler) {
          setupLightbox();
        }
      });
    });
  }

  getImageSlides() {
    // Zoek alle image slides in de main swiper
    const mainSwiper = this.querySelector(".media-main-swiper");
    if (!mainSwiper) return [];
    
    // Zoek eerst de swiper-wrapper (kan direct in mainSwiper zijn of in een child)
    const swiperWrapper = mainSwiper.querySelector(".swiper-wrapper") || 
                          mainSwiper.querySelector(".swiper-wrapper-main .swiper-wrapper");
    if (!swiperWrapper) {
      // Fallback: zoek direct in mainSwiper
      return Array.from(
        mainSwiper.querySelectorAll('[media-gallery="image"]')
      ).filter(slide => {
        return !slide.classList.contains("swiper-slide-duplicate");
      });
    }
    
    return Array.from(
      swiperWrapper.querySelectorAll('[media-gallery="image"]')
    ).filter(slide => {
      // Filter duplicates uit bij loop
      return !slide.classList.contains("swiper-slide-duplicate");
    });
  }

  openLightbox(startIndex = 0) {
    const imageSlides = this.getImageSlides();
    if (!imageSlides.length) return;

    // Haal alle image URLs op
    const imagesData = imageSlides.map(slide => {
      const img = slide.querySelector("img");
      if (!img) return null;
      
      let src = '';
      const srcset = img.getAttribute('data-srcset') || img.srcset;
      if (srcset) {
         const sources = srcset.split(',').map(s => s.trim()).filter(Boolean);
         if (sources.length > 0) {
             let parsed = sources.map(s => {
                 const parts = s.split(' ');
                 return { url: parts[0], width: parseInt(parts[1]) || 0 };
             });
             parsed.sort((a, b) => b.width - a.width); // Sorteer van groot naar klein
             src = parsed[0].url;
         }
      }
      
      if (!src) {
         src = img.getAttribute('data-src') || img.currentSrc || img.src;
      }

      if (src) {
        // Verwijder oude Shopify size suffixes (zoals _400x of _crop_center)
        src = src.replace(/_[0-9]+x[0-9]*(_crop_[a-z]+)?(\.[a-zA-Z0-9]+)(\?|$)/i, '$2$3');
        // Verwijder nieuwe Shopify width parameters (zoals &width=10 of ?width=10)
        src = src.replace(/([?&])width=[0-9]+/i, '');
      }
      
      return { src, alt: img.alt || "" };
    }).filter(Boolean);

    if (!imagesData.length) return;

    let currentIndex = Math.max(0, Math.min(startIndex, imagesData.length - 1));

    // Verwijder eventuele oude lightbox
    const oldOverlay = document.getElementById('loreyna-custom-lightbox');
    if (oldOverlay) oldOverlay.remove();

    if (typeof NextSkyTheme !== 'undefined' && NextSkyTheme.pauseAllMedia) {
      NextSkyTheme.pauseAllMedia(this);
    }

    // Maak een robuuste, fullscreen container aan
    const overlay = document.createElement('div');
    overlay.id = 'loreyna-custom-lightbox';
    // Style de overlay
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100dvh',
      backgroundColor: '#FFFFFF',
      zIndex: '999999',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: '0',
      transition: 'opacity 0.25s ease-in-out',
      overscrollBehavior: 'none'
    });

    // Blokkeer achtergrond scrollen
    document.body.style.overflow = 'hidden';

    // Afbeelding element
    const imgEl = document.createElement('img');
    Object.assign(imgEl.style, {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      display: 'block',
      margin: 'auto',
      opacity: '0',
      transition: 'opacity 0.2s ease-in-out'
    });
    overlay.appendChild(imgEl);

    let fadeTimeout;
    let loadToken = 0;
    const updateImage = (index) => {
      const myToken = ++loadToken;
      const data = imagesData[index];
      imgEl.style.opacity = '0';
      clearTimeout(fadeTimeout);

      // Preload de nieuwe afbeelding voordat we src swappen,
      // zodat we nooit de oude src op opacity 1 zien staan.
      const preload = new Image();
      const swap = () => {
        if (myToken !== loadToken) return; // nieuwere navigatie heeft voorrang
        fadeTimeout = setTimeout(() => {
          if (myToken !== loadToken) return;
          imgEl.src = data.src;
          imgEl.alt = data.alt;
          imgEl.style.opacity = '1';
        }, 150);
      };
      preload.onload = swap;
      preload.onerror = swap;
      preload.src = data.src;
      // Als al in cache: complete is true en onload firet niet altijd
      if (preload.complete) swap();
    };

    updateImage(currentIndex);

    // Sluit knop
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '<svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M1 13L13 1" stroke="#111111" stroke-width="1.5" stroke-linecap="round"/></svg>';
    Object.assign(closeBtn.style, {
      position: 'absolute',
      top: '16px',
      right: '16px',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: '#FFFFFF',
      border: 'none',
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: '1000000',
      padding: '0'
    });
    
    const closeLightbox = () => {
      overlay.style.opacity = '0';
      document.body.style.overflow = '';
      setTimeout(() => overlay.remove(), 250);
    };
    closeBtn.onclick = closeLightbox;
    overlay.appendChild(closeBtn);

    // Navigatie pijltjes (alleen als er meer dan 1 foto is)
    if (imagesData.length > 1) {
      const prevBtn = document.createElement('button');
      prevBtn.innerHTML = '<svg width="9" height="13" viewBox="0 0 8 12" fill="none"><path d="M6.5 10.5L2 6L6.5 1.5" stroke="#111111" stroke-width="1.5" stroke-linecap="square"/></svg>';
      Object.assign(prevBtn.style, {
        position: 'absolute',
        top: '50%',
        left: '16px',
        transform: 'translateY(-50%)',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: '#FFFFFF',
        border: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: '1000000',
        padding: '0'
      });
      prevBtn.onclick = (e) => {
        e.stopPropagation();
        currentIndex = (currentIndex > 0) ? currentIndex - 1 : imagesData.length - 1;
        updateImage(currentIndex);
      };
      overlay.appendChild(prevBtn);

      const nextBtn = document.createElement('button');
      nextBtn.innerHTML = '<svg width="9" height="13" viewBox="0 0 8 12" fill="none"><path d="M1.5 1.5L6 6L1.5 10.5" stroke="#111111" stroke-width="1.5" stroke-linecap="square"/></svg>';
      Object.assign(nextBtn.style, {
        position: 'absolute',
        top: '50%',
        right: '16px',
        transform: 'translateY(-50%)',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: '#FFFFFF',
        border: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: '1000000',
        padding: '0'
      });
      nextBtn.onclick = (e) => {
        e.stopPropagation();
        currentIndex = (currentIndex < imagesData.length - 1) ? currentIndex + 1 : 0;
        updateImage(currentIndex);
      };
      overlay.appendChild(nextBtn);

      // Swipe functionaliteit voor mobiel
      let lbTouchStartX = 0;
      let lbTouchStartY = 0;
      let isDragging = false;

      overlay.addEventListener('touchstart', e => {
        lbTouchStartX = e.changedTouches[0].screenX;
        lbTouchStartY = e.changedTouches[0].screenY;
        isDragging = false;
      }, { passive: true });
      
      overlay.addEventListener('touchmove', e => {
        isDragging = true;
      }, { passive: true });

      overlay.addEventListener('touchend', e => {
        const touchEndX = e.changedTouches[0].screenX;
        const touchEndY = e.changedTouches[0].screenY;
        const deltaX = lbTouchStartX - touchEndX;
        const deltaY = lbTouchStartY - touchEndY;
        
        // Zorg dat we alleen horizontaal swipen (meer horizontale dan verticale verplaatsing)
        if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
           if (deltaX > 0) nextBtn.click(); // Swipe links
           else prevBtn.click(); // Swipe rechts
        }
      }, { passive: true });
    }

    // Klik buiten afbeelding om te sluiten
    overlay.addEventListener('click', (e) => {
      // Voorkom sluiten als er net geswiped/gedragd is
      if (isDragging) {
         isDragging = false;
         return;
      }
      if (e.target === overlay) closeLightbox();
    });

    document.body.appendChild(overlay);

    // Animatie in
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
    });
  }
}
if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}

class GridGallery extends MediaGallery {
  constructor() {
    super();
    this.MediaGalleryHtml = this.innerHTML;
    this.init();
  }

  init() {
    if (this.MediaGalleryHtml) {
      let width = window.innerWidth;
      window.addEventListener("resize", () => {
        const newWidth = window.innerWidth;
        if (newWidth <= 767 && width > 767) {
          this.actionOnMobile();
        }
        if (newWidth > 767 && width <= 767) {
          this.actionOutMobile();
        }
        width = newWidth;
      });
      if (width <= 767) {
        this.actionOnMobile();
      } else {
        this.actionOutMobile();
      }
    }
  }

  actionOnMobile() {
    this.classList.add("swiper");
    this.classList.remove("grid", "grid-cols", "stacked");
    
    const actions = this.querySelector(".swiper-actions")?.outerHTML || '';
    const pagination = this.querySelector(".swiper-pagination")?.outerHTML || '';
    const html = this.MediaGalleryHtml.replaceAll("grid-item", "swiper-slide")
      .replaceAll(actions, "")
      .replaceAll(pagination, "");
    
    const wrapper = `${actions}
    <div class="swiper-wrapper media-main-swiper">
      ${html}
    </div>
    ${pagination}`;
    
    this.innerHTML = wrapper;
    this.initSlideMediaGallery("grid");
    new LazyLoader(".image-lazy-load");
  }

  actionOutMobile() {
    this.classList.remove("swiper");
    if (this.classList.contains("grid-swiper-mobile")) {
      this.classList.add("grid", "grid-cols");
    } else {
      this.classList.add("stacked", "grid");
    }
    this.innerHTML = this.MediaGalleryHtml;
    new LazyLoader(".image-lazy-load");
  }
}

if (!customElements.get("grid-gallery")) {
  customElements.define("grid-gallery", GridGallery);
}

class QuickViewGallery extends GridGallery {
  constructor() {
    super();
    this.MediaGalleryHtml = this.innerHTML;
    this.quickView = document.querySelector("quickview-drawer");
    this.slide = null;
    this.initQuickView();
  }

  initQuickView() {
    if (this.MediaGalleryHtml) {
      let width = window.innerWidth;
      window.addEventListener("resize", () => {
        const newWidth = window.innerWidth;
        if (newWidth <= 767 && width > 767) {
          eventModal(this.quickView, "close", false);
        }
        if (newWidth > 767 && width <= 767) {
          eventModal(this.quickView, "close", false);
        }
        width = newWidth;
      });
      if (width <= 767) {
        this.actionOnMobile();
      } else {
        this.actionOutMobile();
      }
    }
  }

  async actionOnMobile() {
    this.slide = this.initSlideMediaGallery("QuickView");
    this.style.maxHeight = "auto";
    this.style.minHeight = "auto";
  }

  async actionOutMobile() {
    this.slide = this.initSlideMediaGallery("QuickView");
    this.style.maxHeight =
      this.closest(".drawer__body").offsetHeight - 40 + "px";
    this.style.minHeight = "calc(100vh - 40px)";
  }
}

if (!customElements.get("quick-view-gallery")) {
  customElements.define("quick-view-gallery", QuickViewGallery);
}
